import { createCodedError } from '~/common/errors/CodedError';
import {
  JsonApiError,
  JsonApiErrorFormat,
} from '~/services/errors/JsonApiError';

const isJsonApiErrorData = (
  data: unknown,
): data is { errors: JsonApiErrorFormat[] } =>
  typeof data === 'object' &&
  data !== null &&
  Array.isArray((data as { errors?: unknown }).errors);

/**
 * Reconstructs the `JsonApiError` the legacy hand-written atlas methods threw
 * (unwrapped, straight from the axios errorHandler) from the `{ status, data }`
 * shape `atlasBaseQuery` produces, so consumers that never caught an error
 * themselves (e.g. `error instanceof JsonApiError`) keep behaving identically.
 */
export const mapAtlasError = (error: unknown): JsonApiError => {
  if (error instanceof JsonApiError) {
    return error;
  }

  if (isJsonApiErrorData((error as { data?: unknown } | undefined)?.data)) {
    return new JsonApiError(
      (error as { data: { errors: JsonApiErrorFormat[] } }).data.errors,
    );
  }

  return new JsonApiError(undefined as unknown as JsonApiErrorFormat[]);
};

/**
 * Matches the try/catch in the legacy `createPlaybook`/`updatePlaybook`/
 * `validateImportPlaybook` methods: a 4xx JSON:API error becomes a
 * `CodedError` carrying the server's `detail` message; anything else
 * (5xx, network failure) is rethrown as the plain `JsonApiError`.
 */
export const mapAtlasErrorToCodedIfClientError = (
  error: unknown,
): JsonApiError | ReturnType<typeof createCodedError> => {
  const jsonApiError = mapAtlasError(error);
  const [firstError] = jsonApiError.errors;

  if (firstError) {
    const statusCode = parseInt(firstError.status, 10);
    if (statusCode >= 400 && statusCode < 500) {
      return createCodedError({
        message: firstError.detail,
        statusCode,
        code: firstError.code,
      });
    }
  }

  return jsonApiError;
};
